import {
  all,
  takeEvery,
  put,
  select,
  take,
  takeLatest,
  fork,
  call,
  race,
} from 'redux-saga/effects';
import {
  PapiDeviation,
  PapiGallection,
  PapiRequestStudioSettings,
  PapiStudioPagesDrafts,
  PapiStudioPagesMultisubmit,
  PapiStudioPagesProducts,
  PapiStudioPagesPublished,
  PapiStudioPagesScheduled,
  PapiStudioPagesStash,
} from '@wix/da-papi-types';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import {
  getNextOffset,
  getPrevOffset,
} from '@wix/da-shared-react/pkg/Stream/withOffset/selectors';
import {
  getItemsPerFetch,
  getStream,
} from '@wix/da-shared-react/pkg/Stream/base/selectors';
import {
  takeStreamFetch,
  fetchStreamContents,
} from '@wix/da-shared-react/pkg/Stream/helpers';
import { addNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { NotificationType } from '@wix/da-shared-react/pkg/Notifications';
import {
  deleteDeviationSuccess,
  unpublishDeviationSuccess,
} from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import { deleteTierSuccess } from '@wix/da-shared-react/pkg/Tier/redux/creator/actions';
import { uploadSuccess } from '@wix/da-shared-react/pkg/redux/uploads/actions';
import { putEntities } from '@wix/da-shared-react/pkg/redux/entities/actions';
import {
  normalizeDeviations,
  normalizeGallections,
} from '@wix/da-shared-react/pkg/redux/normalizr/helpers';
import { deviationExtendedSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import {
  submitDeviationSuccess,
  publishDeviationSuccess,
} from '@wix/da-submit-shared/pkg/redux/actions';
import {
  initialize,
  fetch,
} from '@wix/da-shared-react/pkg/Stream/withOffset/actions';
import { deleteStream } from '@wix/da-shared-react/pkg/Stream/base/actions';
import type { OffsetStream } from '@wix/da-shared-react/pkg/Stream/withOffset/types';
import {
  refreshTable,
  refreshTableSuccess,
  fetchTableFirstPage,
  fetchTableLastPage,
  fetchTableNext,
  fetchTablePrevious,
  refreshTableError,
  reorderRow,
  reorderMulti,
  setTableLayout,
} from '../../actions/studio';
import {
  getPageSize,
  getPlaygroundSubsection,
  getResultsTotalCount,
  getSelectedFolder,
  getDuperbrowseStreamVersion,
  getCurrentProductsSectionType,
  getActiveSection,
  getStudioParams,
  getTableStreamId,
} from '../../selectors/studio';
import {
  DREAMUP_FOLDER_NAME,
  JOURNAL_DRAFTS_FOLDER_NAME,
  LITERATURE_DRAFTS_FOLDER_NAME,
  MURO_FOLDER_NAME,
  SAVED_SUBMISSIONS_FOLDER_NAME,
  SEARCHABLE_SECTIONS,
  STASH_SUBFOLDERS_STREAM,
  WIXEL_FOLDER_NAME,
} from '../../../constants';
import { NotificationType as SubmitNotificationType } from '../../types/notifications';
import { PlaygroundSubsection, StudioSection } from '../../types/store';
import { ProductType } from '../../components/products/types';

type StudioPageResponse =
  | PapiStudioPagesDrafts
  | PapiStudioPagesMultisubmit
  | PapiStudioPagesScheduled
  | PapiStudioPagesPublished
  | PapiStudioPagesStash
  | PapiStudioPagesProducts;

function* fetchDeviationsOffset(offset: number, preserveSelection?: boolean) {
  const streamId = yield select(getTableStreamId);
  const itemsPerFetch = yield select(state =>
    getItemsPerFetch(state, streamId)
  );
  const {
    section,
    deviationIds,
    folderIds,
    searchQuery,
    subfolderId,
    tierId,
    contentType,
    unlisted,
  } = yield select(getStudioParams);
  const playgroundSubsection = yield select(getPlaygroundSubsection);
  const searchableFolder = SEARCHABLE_SECTIONS.includes(section);
  const duperbrowseStreamVersion = yield select(getDuperbrowseStreamVersion);

  let papiEndpointUrl;
  const params = {
    init: false,
    deviations_offset: offset,
  };

  if (searchQuery && searchQuery !== '') {
    params['search_query'] = searchQuery;
  }

  switch (section) {
    case StudioSection.PUBLISHED:
      if (tierId) {
        papiEndpointUrl = '/studio/pages/tier';
        params['tierid'] = tierId;
      } else {
        const publishedSelectedFolder: PapiGallection =
          yield select(getSelectedFolder);
        papiEndpointUrl = '/studio/pages/published';
        // Only set all_folder to true if no specific folder is selected
        params['all_folder'] = !subfolderId;
        if (subfolderId && publishedSelectedFolder) {
          params['folderid'] = publishedSelectedFolder.folderId;
        }
      }
      if (contentType) {
        params['content_type'] = contentType;
      }
      break;

    case StudioSection.SCHEDULED:
      papiEndpointUrl = '/studio/pages/scheduled';
      break;

    case StudioSection.STASH:
      const selectedFolder: PapiGallection = yield select(getSelectedFolder);
      papiEndpointUrl = '/studio/pages/stash';
      params['folderid'] = subfolderId ? selectedFolder.folderId : undefined;
      break;

    case StudioSection.PLAYGROUND:
      papiEndpointUrl = '/studio/pages/playground';
      switch (playgroundSubsection) {
        case PlaygroundSubsection.Wixel:
          params['root_foldername'] = WIXEL_FOLDER_NAME;
          break;
        case PlaygroundSubsection.Draw:
          params['root_foldername'] = MURO_FOLDER_NAME;
          break;
        case PlaygroundSubsection.DreamUp:
        default:
          params['root_foldername'] = DREAMUP_FOLDER_NAME;
          break;
      }
      break;

    case StudioSection.PRODUCTS_COMMISSION:
    case StudioSection.PRODUCTS_ORIGINAL:
    case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
    case StudioSection.PRODUCTS_PREMIUM_GALLERY:
    case StudioSection.PRODUCTS_EXCLUSIVE:
    case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
    case StudioSection.PRODUCTS_PCP:
    case StudioSection.PRODUCTS_PCP_UNLISTED:
    case StudioSection.PRODUCTS_TIER:
    case StudioSection.PRODUCTS_TIER_UNLISTED:
      const productType = yield select(getCurrentProductsSectionType);
      papiEndpointUrl = '/studio/pages/products';
      params['product_type'] = productType;
      params['unlisted'] = unlisted;
      break;

    case StudioSection.APPS:
    case StudioSection.APPS_UNLISTED:
      papiEndpointUrl = '/studio/pages/apps';
      params['published'] = !unlisted;
      break;

    default:
      if (deviationIds || folderIds) {
        papiEndpointUrl = '/studio/pages/multisubmit';
        params['deviationids'] = deviationIds;
        params['folderids'] = folderIds;
      } else {
        papiEndpointUrl = '/studio/pages/drafts';
        if (section === StudioSection.LITERATURE) {
          params['root_foldername'] = LITERATURE_DRAFTS_FOLDER_NAME;
        } else if (section === StudioSection.JOURNALS) {
          params['root_foldername'] = JOURNAL_DRAFTS_FOLDER_NAME;
        } else {
          params['root_foldername'] = SAVED_SUBMISSIONS_FOLDER_NAME;
        }
      }
      break;
  }

  yield fetchStreamContents<any, any>({
    streamId,
    url: papiEndpointUrl,
    papiProject: 'v1',
    params,
    onSuccess: function* (response: StudioPageResponse) {
      const { rootFolder, subfolders, freeCapacity, maxCapacity, counts } =
        response;
      const deviations: PapiDeviation[] = [];
      const allTags = {};
      const appData = {};
      const originalsData = {};

      response.deviations.studioResults?.forEach(
        ({ deviation, tags, app, originals }) => {
          deviations.push(deviation);
          if (tags) {
            allTags[deviation.deviationId] = tags;
          }
          if (app) {
            appData[deviation.deviationId] = app;
          }
          if (originals) {
            originalsData[deviation.deviationId] = originals;
          }
        }
      );

      if (deviations.length > 0) {
        const { entities } = normalizeDeviations(deviations);
        yield put(putEntities({ entities }));
      }

      const duperbrowseStreamId = [
        'studio',
        deviations.length > 0 ? deviations[0].deviationId : 0,
        duperbrowseStreamVersion,
      ].join('-');
      yield put(deleteStream(duperbrowseStreamId));

      // Deviation deletion is an async operation, and the rootFolder.size
      // might return stale information, which is especially noticeable when
      // you delete the last batch of deviations, since it returns non-zero
      // size.
      let resultsTotalCount =
        searchableFolder && searchQuery
          ? response.deviations.estTotal
          : rootFolder?.size;

      switch (section) {
        case StudioSection.PRODUCTS_COMMISSION:
          resultsTotalCount = counts?.commission ?? 0;
          break;
        case StudioSection.PRODUCTS_ORIGINAL:
        case StudioSection.PRODUCTS_ORIGINAL_UNLISTED:
          resultsTotalCount = counts?.originals ?? 0;
          break;
        case StudioSection.PRODUCTS_PREMIUM_GALLERY:
          resultsTotalCount = counts?.premiumGallery ?? 0;
          break;
        case StudioSection.PRODUCTS_TIER:
        case StudioSection.PRODUCTS_TIER_UNLISTED:
          resultsTotalCount = counts?.tier ?? 0;
          break;
        case StudioSection.PRODUCTS_EXCLUSIVE:
        case StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED:
          resultsTotalCount = counts?.exclusive ?? 0;
          break;
        case StudioSection.PRODUCTS_PCP:
        case StudioSection.PRODUCTS_PCP_UNLISTED:
          resultsTotalCount = counts?.pcp ?? 0;
          break;
        case StudioSection.PUBLISHED:
          const { estTotal } = response.deviations;
          resultsTotalCount =
            (estTotal || 0) > itemsPerFetch ? estTotal : rootFolder?.size;
          break;
      }

      yield put(
        refreshTableSuccess({
          freeCapacity: freeCapacity ?? 0,
          maxCapacity: maxCapacity ?? 0,
          resultsTotalCount: resultsTotalCount ?? 0,
          tags: allTags,
          originalsData,
          appData,
          selectedFolder: rootFolder,
          preserveSelection,
        })
      );

      if (section === StudioSection.STASH) {
        yield put(
          initialize({
            streamId: STASH_SUBFOLDERS_STREAM.streamId,
            streamParams: {
              folderId: rootFolder?.folderId,
              username: rootFolder?.owner.username,
            },
            itemsPerFetch: response.deviations.pageSize!,
            items: subfolders?.results ?? [],
            hasMore: subfolders?.hasMore,
            nextOffset: subfolders?.nextOffset,
          })
        );
      }
    },
    onFailure: function* (papiError) {
      yield put(refreshTableError(papiError));
      yield put(
        addNotification(NotificationType.Generic, {
          messageLocaleKey: 'studio.notifications.refreshTable.error',
        })
      );
    },
    getStreamData: (response: any) => {
      const { hasMore, nextOffset } = response.deviations;

      /**
       * This can happen in some edge cases around users manually manipulating the stash folder we save
       * these draft submissions in.
       */
      if (!response.deviations.studioResults) {
        return {
          hasMore: false,
          hasLess: false,
          items: [],
          prevOffset: 0,
          reset: true,
        };
      }

      const results = response.deviations.studioResults.map(r => r.deviation);
      const { result: normalizedDeviationIds } = normalizeDeviations(results);
      return {
        hasMore: !!hasMore,
        hasLess: offset > 0,
        items: normalizedDeviationIds,
        prevOffset: Math.max(0, offset - response.deviations.pageSize),
        nextOffset,
        reset: true,
      };
    },
  });
}

function* fetchPremiumGalleriesOffset(
  offset: number,
  preserveSelection?: boolean
) {
  const streamId = yield select(getTableStreamId);
  const pageSize = yield select(getPageSize);
  const papiEndpointUrl = '/studio/pages/products';
  const params = {
    init: false,
    deviations_offset: offset,
    deviations_limit: pageSize,
    product_type: ProductType.PremiumGallery,
    with_subfolder_size: true,
  };

  yield fetchStreamContents<any, any>({
    streamId,
    url: papiEndpointUrl,
    papiProject: 'v1',
    params,
    onSuccess: function* (response: StudioPageResponse) {
      const { rootFolder, subfolders, freeCapacity, maxCapacity, counts } =
        response;
      const resultsTotalCount = counts?.premiumGallery ?? 0;
      const galleries: PapiGallection[] = subfolders?.results ?? [];
      if (galleries.length > 0) {
        const { entities } = normalizeGallections(galleries);
        yield put(putEntities({ entities }));
      }

      yield put(
        refreshTableSuccess({
          freeCapacity: freeCapacity ?? 0,
          maxCapacity: maxCapacity ?? 0,
          resultsTotalCount: resultsTotalCount ?? 0,
          tags: {},
          originalsData: {},
          selectedFolder: rootFolder,
          preserveSelection,
        })
      );
    },
    onFailure: function* (papiError) {
      yield put(refreshTableError(papiError));
      yield put(
        addNotification(NotificationType.Generic, {
          messageLocaleKey: 'studio.notifications.refreshTable.error',
        })
      );
    },
    getStreamData: (response: PapiStudioPagesProducts) => {
      const { hasMore, nextOffset, results = [] } = response.subfolders ?? {};
      const { result: normalizedDeviationIds } = normalizeGallections(results);
      return {
        hasMore: !!hasMore,
        hasLess: offset > 0,
        items: normalizedDeviationIds,
        prevOffset: Math.max(0, offset - pageSize),
        nextOffset,
        reset: true,
      };
    },
  });
}

function* fetchTableOffset(offset: number, preserveSelection?: boolean) {
  const activeSection = yield select(getActiveSection);
  if (activeSection === StudioSection.PRODUCTS_PREMIUM_GALLERY) {
    return yield call(fetchPremiumGalleriesOffset, offset, preserveSelection);
  }
  return yield call(fetchDeviationsOffset, offset, preserveSelection);
}

function* handleRefreshTable(action: ReturnType<typeof refreshTable>) {
  const { preservePage, preserveSelection } = action.payload;
  const pageSize = yield select(getPageSize);
  const streamId = yield select(getTableStreamId);
  const stream: OffsetStream = yield select(getStream, streamId);
  const currentOffset =
    // Checking both prevOffset and hasLess here because prevOffset is a bit sticky. Navigating away from the first page
    // then back to the first page does not reset prevOffset to undefined. Still checking it here for typescript reasons,
    // though.
    stream.prevOffset === undefined || !stream.hasLess
      ? 0
      : stream.prevOffset + pageSize;

  yield fetchTableOffset(preservePage ? currentOffset : 0, preserveSelection);
}

function* onRefreshSuccess(callback) {
  const { success } = yield race({
    success: take(refreshTableSuccess),
    error: take(refreshTableError),
  });

  if (success) {
    yield call(callback);
  }
}

function* handleFetchTableNext({
  payload: { streamId, onSuccess },
}: ReturnType<typeof fetchTableNext>) {
  yield put(fetch(streamId)); // set loading state on stream
  yield fork(onRefreshSuccess, onSuccess);
  yield fetchTableOffset(yield select(getNextOffset, streamId));
}

function* handleFetchTablePrevious({
  payload: { streamId, onSuccess },
}: ReturnType<typeof fetchTablePrevious>) {
  yield put(fetch(streamId)); // set loading state on stream
  yield fork(onRefreshSuccess, onSuccess);
  yield fetchTableOffset(yield select(getPrevOffset, streamId));
}

function* handleFetchTableFirstPage({
  payload: { streamId, onSuccess },
}: ReturnType<typeof fetchTableFirstPage>) {
  yield put(fetch(streamId)); // set loading state on stream
  yield fork(onRefreshSuccess, onSuccess);
  yield fetchTableOffset(0);
}

function* handleFetchTableLastPage({
  payload: { streamId, onSuccess },
}: ReturnType<typeof fetchTableLastPage>) {
  const pageSize = yield select(getPageSize);
  const resultsTotalCount: number = yield select(getResultsTotalCount);
  const pageCount = Math.ceil(resultsTotalCount / pageSize);
  const offset = pageCount * pageSize - pageSize;
  yield put(fetch(streamId)); // set loading state on stream
  yield fork(onRefreshSuccess, onSuccess);
  yield fetchTableOffset(offset);
}

/* When a draft is uploaded, saved, published or deleted we refresh the table */
function* handleDraftsUpdate() {
  const selectedCount = yield select(state => {
    const selection = state.studio.selection;
    return selection.selectedRows.length;
  });
  const resultsTotalCount: number = yield select(getResultsTotalCount);

  // If we're deleting all items, don't preserve the page to avoid errors
  // when trying to fetch from an empty folder at a non-zero offset
  const preservePage = selectedCount < resultsTotalCount;

  yield put(refreshTable({ preservePage }));
}

function* handleDeviationSubmitted(
  deviation: PapiDeviation,
  isPublished: boolean
) {
  yield put(
    putEntities({
      entities: {
        [deviationExtendedSchema.key]: {
          [deviation.deviationId]: undefined,
        },
      },
    })
  );
  yield put(refreshTable({ preservePage: true }));
  yield put(
    addNotification(SubmitNotificationType.DEVIATION_SUBMIT, {
      deviation,
      isPublish: isPublished,
    })
  );
}

function* handleSubmitDeviationSuccess({
  payload: { contextId, deviation, isPublished },
}: ReturnType<typeof submitDeviationSuccess>) {
  if (contextId === 'submit-form') {
    yield call(handleDeviationSubmitted, deviation, isPublished);
  }
}

function* handlePublishDeviationSuccess({
  payload: { deviation },
}: ReturnType<typeof publishDeviationSuccess>) {
  yield call(handleDeviationSubmitted, deviation, true);
}

function* callReorder(folderId, deviationId, position) {
  const activeSection = yield select(getActiveSection);
  const folderType =
    activeSection === StudioSection.PUBLISHED ? 'gallery' : 'stash';

  const response = yield call(requestPuppy, {
    method: 'post',
    url: 'gallection/folders/update_deviation_order',
    data: {
      type: folderType, // Use gallery for PUBLISHED, stash for others
      folderid: folderId,
      deviationid: deviationId,
      position,
    },
  });
  return response && !isApiError(response);
}

function* handleReorderRow({
  payload: { folderId, deviationId, position },
}: ReturnType<typeof reorderRow>) {
  const success = yield call(callReorder, folderId, deviationId, position);
  if (!success) {
    yield put(
      addNotification(NotificationType.Generic, {
        messageLocaleKey: 'studio.notifications.reorder.error',
      })
    );
  } else {
    yield put(refreshTable({ preservePage: true }));
  }
}

function* handleReorderMulti({
  payload: { folderId, deviationIds },
}: ReturnType<typeof reorderMulti>) {
  const results: boolean[] = [];
  for (let i = 0; i < deviationIds.length; i++) {
    const { deviationId, position } = deviationIds[i];
    const result = yield call(callReorder, folderId, deviationId, position);
    results.push(result);
  }

  if (results.filter(i => i).length !== deviationIds.length) {
    yield put(
      addNotification(NotificationType.Generic, {
        messageLocaleKey: 'studio.notifications.reorder.error',
      })
    );
  } else {
    yield put(refreshTable({ preservePage: true }));
  }
}

function* handleSetTableLayout({
  payload: { layout },
}: ReturnType<typeof setTableLayout>) {
  const response = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'studio/settings',
      data: {
        table_layout: layout,
      } as PapiRequestStudioSettings,
    },
    undefined,
    'v1'
  );

  if (!response || isApiError(response)) {
    yield put(
      addNotification(NotificationType.Generic, {
        messageLocaleKey: 'studio.notifications.settings.error',
      })
    );
  }
}

export default function* tableSaga() {
  yield all([
    takeEvery(refreshTable, handleRefreshTable),
    takeEvery(reorderRow, handleReorderRow),
    takeEvery(reorderMulti, handleReorderMulti),
    takeStreamFetch(fetchTableNext, handleFetchTableNext),
    takeStreamFetch(fetchTablePrevious, handleFetchTablePrevious),
    takeStreamFetch(fetchTableFirstPage, handleFetchTableFirstPage),
    takeStreamFetch(fetchTableLastPage, handleFetchTableLastPage),
    takeLatest(uploadSuccess, handleDraftsUpdate),
    takeLatest(deleteDeviationSuccess, handleDraftsUpdate),
    takeLatest(deleteTierSuccess, handleDraftsUpdate),
    takeLatest(unpublishDeviationSuccess, handleDraftsUpdate),
    takeLatest(submitDeviationSuccess, handleSubmitDeviationSuccess),
    takeLatest(publishDeviationSuccess, handlePublishDeviationSuccess),
    takeLatest(setTableLayout, handleSetTableLayout),
  ]);
}
